// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify";
import xngGaCheckoutStepsEvent from "@guestsupply/App/Handlers/GoogleAnalytics/Checkout/CheckoutStepsEvent";
import { AddressValidationCandidate } from "@guestsupply/App/Services/AddressValidationService";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BillToModel,
    CartModel,
    CountryModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import BillToSelector, { BillToSelectorStyles } from "@insite/content-library/Components/BillToSelector";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import Modals from "@insite/content-library/Components/Modals";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/BillingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import parse from "html-react-parser";
import React, { Dispatch, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyAction } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address: BillToModel;
    onChange: (address: BillToModel, isEdit: boolean) => void;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    extendedStyles?: BillingAddressStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;

    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
        cart,
        cartBillTo: getBillToState(state, cart?.billToId)?.value,
        isAuthenticated: xIsAuthenticated(state),
        addressValidation: (state as CustomAppState).xAddressValidation.addressValidation,
        isSubmitted: (state as CustomAppState).xAddressValidation.isSubmitted,
        billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState,
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        cartId,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    setAddressBookOverlayIsOpen,

    updateBillTo,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setIsSubmitted:
        ({ isSubmitted }: { isSubmitted: boolean }) =>
        (dispatch: Dispatch<AnyAction>) => {
            dispatch({
                type: "xAddressValidation/SetIsSubmittedAddressValidationData",
                isSubmitted,
            });
        },
    clearAddressValidationData: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xAddressValidation/ClearAddressValidationData",
        });
    },
    // xng-change: add google event
    xngGaCheckoutStepsEvent,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    ThemeProps<BaseTheme> &
    HasHistory;

export interface BillingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    billToSelector?: BillToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressForm?: BillingAddressFormStyles;
    addressDisplay?: AddressInfoDisplayStyles;
    editAddressForm?: CustomerAddressFormStyles;
    // XNG-CHANGES
    headingWrapper?: InjectableCss;
    noteswWrapper?: InjectableCss;
    notify?: TypographyProps;
    addressTypographyStyles?: TypographyProps;
    buttonsWrapper?: InjectableCss;
    resultsWrapper?: InjectableCss;
    radioStyles?: RadioProps;
    checkoutShippingPageModal?: ModalPresentationProps;
}

export const billingAddressStyles: BillingAddressStyles = {};

const BillingAddress = ({
    address,
    onChange,
    countries,
    addressFieldDisplayCollection,
    extendedStyles,
    currentUserIsGuest,
    toaster,
    isBillingAddressUpdateRequired,
    cart,
    cartBillTo,
    theme,
    setAddressBookOverlayIsOpen,
    isAuthenticated,
    // XNG-CHANGES
    addressValidation,
    billingAddressFormState,
    updateBillTo,
    history,
    cartPageLink,
    updateCart,
    cartId,
    reviewAndSubmitPageLink,
    cartPageUrl,
    isSubmitted,
    setIsSubmitted,
    clearAddressValidationData,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaCheckoutStepsEvent,
}: Props) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isAddressValidationModalOpen, setIisAddressValidationModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<BillToModel | undefined>(undefined);
    const [selectedAddress, setSelectedAddress] = useState<AddressValidationCandidate | undefined>(undefined);
    const [radioValue, setRadioValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [someCartLinesNotValidForWarehouse, setSomeCartLinesNotValidForWarehouse] = useState(false);
    const [insufficientInventoryAtCheckout, setInsufficientInventoryAtCheckout] = useState(false);

    const candidatesLength = useRef<number>(addressValidation?.candidates?.length || 0);
    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);
    // XNG-CHANGES
    useEffect(() => {
        if (!addressValidation?.candidates?.length) {
            candidatesLength.current = 0;
        }

        if (
            cart &&
            !cart.canCheckOut &&
            cart.properties.someCartLinesNotValidForWarehouse === "true" &&
            !cart.cartLines?.some(o => o.cantBuy)
        ) {
            setSomeCartLinesNotValidForWarehouse(true);
        } else if (cart && !cart.canCheckOut && !cart.cartLines?.some(o => o.cantBuy)) {
            setInsufficientInventoryAtCheckout(true);
        } else if (
            !!addressValidation?.candidates?.length &&
            addressValidation?.candidates?.length !== candidatesLength.current
        ) {
            candidatesLength.current = addressValidation?.candidates?.length || 0;

            setIisAddressValidationModalOpen(true);
            if (addressValidation?.candidates?.length === 1) {
                setRadioValue("0");
                setSelectedAddress(addressValidation?.candidates[0] as any);
            }
        }
    }, [addressValidation.candidates, cart, cart?.properties]);

    const styles = mergeToNew(billingAddressStyles, extendedStyles);

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);
    // XNG-CHANGES
    const addressValidationModalCloseHandler = () => {
        setIisAddressValidationModalOpen(false);
        clearAddressValidationData();
    };

    const selectCustomerHandler = (billTo: BillToModel) => {
        customerSelectorModalCloseHandler();
        onChange(billTo, false);
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BillToModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toaster.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };
    // XNG-CHANGES
    const handleAddressCandidatesChange = (e: any) => {
        setRadioValue(e.target.value);
        if (addressValidation.candidates?.length) {
            if (addressValidation.candidates[Number(e.target.value)]) {
                setSelectedAddress(addressValidation.candidates[Number(e.target.value)] as any);
            } else {
                const address = {
                    address1: billingAddressFormState?.address.address1,
                    address2: billingAddressFormState?.address.address2,
                    city: billingAddressFormState?.address.city,
                    stateProvince: billingAddressFormState?.address.state?.abbreviation,
                    country: billingAddressFormState?.address.country?.abbreviation,
                    phone: billingAddressFormState?.address.phone,
                    postalCode: billingAddressFormState?.address.postalCode,
                    contact: billingAddressFormState?.address.country,
                };
                setSelectedAddress(address as any);
            }
        }
    };

    const handleUpdateCartCompleted = (cart: CartModel | undefined) => {
        if (!cart) {
            return;
        }
        const cantBuy = cart.canCheckOut && cart.cartLines?.some(o => o.cantBuy);
        const someCartLinesNotValidForWarehouse = cart.properties?.someCartLinesNotValidForWarehouse === "true";

        if (!someCartLinesNotValidForWarehouse && !cantBuy) {
            // xng-change: add google event
            xngGaCheckoutStepsEvent({
                checkoutEventType: GOOGLE_EVENTS.ADD_SHIPPING_INFO,
                includeCheckoutEvent: xGoogleAnalytics.includeCheckoutShippingInfoEvent,
            });

            const continueUrl = cartId
                ? `${reviewAndSubmitPageLink!.url}?cartId=${cartId}`
                : reviewAndSubmitPageLink!.url;
            history.push(continueUrl);
        }
    };

    const changeBillingAddressHandler = () => {
        let currentBillTo = billingAddressFormState?.address;
        if (!selectedAddress || !currentBillTo) {
            return;
        }

        setIsLoading(true);

        currentBillTo = {
            ...currentBillTo,
            address1: selectedAddress.address1,
            address2: selectedAddress.address2,
            city: selectedAddress.city,
            postalCode: selectedAddress.postalCode,
            phone: selectedAddress.phone,
            state: {
                ...currentBillTo.state,
                abbreviation: selectedAddress.stateProvince,
            },
            country: {
                ...currentBillTo.country,
                abbreviation: selectedAddress.country,
            },
        } as any;

        updateBillTo({
            billTo: currentBillTo!,
            isEdit: true,
            onSuccess: async hasCartlines => {
                if (!hasCartlines) {
                    history.push(cartPageLink!);
                } else {
                    await updateCart({
                        onComplete: (result: any) => {
                            if (result?.apiResult?.cart) {
                                const updatedCart = result.apiResult?.cart;
                                handleUpdateCartCompleted(updatedCart);
                            }
                        },
                    });
                }
            },
            onComplete: () => {
                setIisAddressValidationModalOpen(false);
                setIsLoading(false);
                setIsSubmitted({ isSubmitted: false });
                clearAddressValidationData();
            },
        });
    };

    const modalCloseHandler = () => {
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }

        setSomeCartLinesNotValidForWarehouse(false);
        setInsufficientInventoryAtCheckout(false);
    };

    return (
        <>
            <GridContainer {...styles.container} data-test-selector="checkoutShipping_billingAddress">
                <GridItem {...styles.headingGridItem}>
                    <StyledWrapper {...styles.headingWrapper}>
                        <Typography {...styles.headingText}>
                            {currentUserIsGuest ? translate("Shipping Address") : translate("Billing Address")}
                        </Typography>
                        {/* {!currentUserIsGuest &&
                            cart?.status !== "QuoteProposed" &&
                            !(cart?.status === "Cart" && cart?.type === "Job") && (
                                <Link
                                    {...styles.selectSavedAddressLink}
                                    type="button"
                                    onClick={selectAddressClickHandler}
                                    data-test-selector="checkoutShipping_selectSavedAddress"
                                >
                                    {translate("Select Saved Address")}
                                    <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                                </Link>
                            )} */}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.addressDisplayAndFormGridItem}>
                    {currentUserIsGuest || isBillingAddressUpdateRequired ? (
                        <>
                            {addressFieldDisplayCollection && countries && (
                                <BillingAddressForm
                                    countries={countries}
                                    fieldDisplay={addressFieldDisplayCollection}
                                    extendedStyles={styles.addressForm}
                                />
                            )}
                            {/* XNG-CHANGES */}
                            {!isAuthenticated && (
                                <>
                                    <StyledWrapper {...styles.noteswWrapper}>
                                        {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>
                                    {!!addressValidation.errorMessage?.length && isSubmitted && (
                                        <>
                                            <Notify
                                                {...styles.notify}
                                                type="danger"
                                                data-test-selector="CheckoutShipping_addressValidationerror"
                                            >
                                                <StyledWrapper>
                                                    {parse(addressValidation.errorMessage, parserOptions)}
                                                </StyledWrapper>
                                            </Notify>
                                            <Notify
                                                {...styles.notify}
                                                type="danger"
                                                data-test-selector="Guest_AddressValidation_Enter_More_Accurate_Address_Msg"
                                            >
                                                {siteMessage("Guest_AddressValidation_Enter_More_Accurate_Address_Msg")}
                                            </Notify>
                                        </>
                                    )}
                                    {!addressValidation.errorMessage?.length &&
                                        (!addressValidation.candidates || addressValidation.candidates.length < 1) &&
                                        isSubmitted && (
                                            <Notify
                                                {...styles.notify}
                                                type="danger"
                                                data-test-selector="Guest_AddressValidation_No_Candidates_Error_Msg"
                                            >
                                                {siteMessage("Guest_AddressValidation_No_Candidates_Error_Msg")}
                                            </Notify>
                                        )}
                                </>
                            )}
                        </>
                    ) : (
                        <AddressInfoDisplay
                            {...address}
                            state={address.state?.abbreviation}
                            country={address.country?.abbreviation}
                            extendedStyles={styles.addressDisplay}
                        />
                    )}
                </GridItem>
            </GridContainer>
            <Modal
                {...styles.addressBookModal}
                headline={translate("Address Book")}
                isOpen={isCustomerSelectorModalOpen}
                handleClose={customerSelectorModalCloseHandler}
                data-test-selector="checkoutShipping_addressModal"
            >
                <BillToSelector
                    currentBillTo={cartBillTo}
                    onSelect={selectCustomerHandler}
                    onEdit={editCustomerHandler}
                    extendedStyles={styles.billToSelector}
                />
                <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                    <Button
                        {...styles.addressBookModalCancelButton}
                        onClick={customerSelectorModalCloseHandler}
                        data-test-selector="addressModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            {addressBeingEdited && (
                <Modal
                    {...styles.addressBookModal}
                    headline={translate("Edit Address")}
                    isOpen={isEditAddressModalOpen}
                    handleClose={editAddressModalCloseHandler}
                    data-test-selector="checkoutShipping_editAddressModal"
                >
                    <CustomerAddressForm
                        address={addressBeingEdited}
                        countries={countries}
                        addressFieldDisplayCollection={addressFieldDisplayCollection}
                        onSubmit={editAddressFormSubmitHandler}
                        saveButtonTextOverride={translate("Save & Apply")}
                        onCancel={editAddressModalCloseHandler}
                        extendedStyles={styles.editAddressForm}
                    />
                </Modal>
            )}
            {/* XNG-CHANGES */}
            {addressValidation.candidates && (
                <Modal
                    headline={translate("Address Verification")}
                    isOpen={isAddressValidationModalOpen}
                    handleClose={addressValidationModalCloseHandler}
                    data-test-selector="checkoutShipping_addressValidationModal"
                    size={600}
                >
                    <LoadingOverlay loading={isLoading}>
                        <Typography as="p">{translate("Please select the correct address")}</Typography>
                        <RadioGroup as="legend" value={radioValue} onChangeHandler={handleAddressCandidatesChange}>
                            <Typography {...styles.addressTypographyStyles}>{translate("You Entered:")}</Typography>
                            <Radio
                                {...styles.radioStyles}
                                value={`${billingAddressFormState?.address.address1}, ${billingAddressFormState?.address.city}, ${billingAddressFormState?.address.state?.abbreviation}, ${billingAddressFormState?.address.postalCode}`}
                            >
                                {`${billingAddressFormState?.address.address1}, ${billingAddressFormState?.address.city}, ${billingAddressFormState?.address.state?.abbreviation}, ${billingAddressFormState?.address.postalCode}`}
                            </Radio>
                            <Typography {...styles.addressTypographyStyles}>{translate("We Found:")}</Typography>
                            <StyledWrapper {...styles.resultsWrapper}>
                                {addressValidation.candidates.map((candidate, id) => (
                                    <Radio {...styles.radioStyles} key={id} value={`${id}`}>
                                        {`${candidate.address1}, ${candidate.city}, ${candidate.stateProvince}, ${candidate.postalCode}`}
                                    </Radio>
                                ))}
                            </StyledWrapper>
                        </RadioGroup>
                        <StyledWrapper {...styles.buttonsWrapper}>
                            <Button
                                variant="secondary"
                                onClick={addressValidationModalCloseHandler}
                                aria-label="Cancel"
                            >
                                {translate("Cancel")}
                            </Button>
                            <Button disabled={!selectedAddress} onClick={changeBillingAddressHandler} aria-label="Save">
                                {translate("Save")}
                            </Button>
                        </StyledWrapper>
                    </LoadingOverlay>
                </Modal>
            )}

            <Modal
                {...styles.checkoutShippingPageModal}
                isOpen={someCartLinesNotValidForWarehouse}
                handleClose={modalCloseHandler}
            >
                <Notify type="danger">
                    {siteMessage(
                        "Guest_CartError_Some_CartLines_Not_Valid_For_Warehouse",
                        "Some of the items in your shopping cart are not available to be shipped to your postal code. Please remove these items to continue.",
                    )}
                </Notify>
            </Modal>
            <Modal
                {...styles.checkoutShippingPageModal}
                isOpen={insufficientInventoryAtCheckout}
                handleClose={modalCloseHandler}
            >
                <Notify type="danger">{siteMessage("Cart_InsufficientInventoryAtCheckout")}</Notify>
            </Modal>
            <Modals />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(withTheme(BillingAddress))));
